import { ArrowCcwIcon, ClockAltIcon, CloseIcon, ExportIcon } from "@xola/icons";
import { PopoverList, SubmitButton } from "@xola/ui-kit";
import { DateFormat, formatDate, isBefore, JsDateRange, now, utcToDate } from "common";
import { isEmpty, omit } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Cron, Report } from "types";
import { api } from "../../../api/api";
import { schedulerApi } from "../../../api/schedulerApi";
import { config } from "../../../config";
import { useSeller, useUser } from "../../../hooks/useAccount";
import { useInvalidate } from "../../../hooks/useInvalidation";
import { useScheduler } from "../../../hooks/useScheduler";
import { getQueryParams } from "../../../models/ReportFilterState";
import { PromptModal } from "../../Common/PromptModal";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { Loader } from "../../Loader";
import { DateRange } from "./CustomReportDateRangePicker";
import { CustomReportExportModal } from "./CustomReportExportModal";
import { ReportFilter, ReportFilterOperator, ReportFilterState, ReportFilterValue } from "./ReportFilterState";
import { SaveReportModal } from "./SaveReportModal";
import { Duration, ScheduleReportModal } from "./ScheduleReportModal";
import { PreviewModeGuard } from "./usePreviewMode";

interface CustomReportActionsProps {
    isFetching: boolean;
    isFilterChanged: boolean;
    report: Report;
    selectedDateField: string;
    filter: ReportFilterState;
    excludedColumns: string[];
    onRun: () => void;
    onDateChange: (value: DateRange) => void;
}

type ModalState = "openForSave" | "openForSaveAs" | "openForSaveSchedule" | "closed";

export const CustomReportActions = ({
    isFetching,
    isFilterChanged,
    report: initialReport,
    filter,
    selectedDateField,
    excludedColumns,
    onDateChange,
    onRun,
}: CustomReportActionsProps) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [modalState, setModalState] = useState<ModalState>("closed");
    const [report, setReport] = useState<Report>(initialReport);
    const [isExportModalOpen, setIsExportModalOpen] = useState(false);

    const isWarning = isFilterChanged || isFetching;

    const getUpdatedFilterForPeriod = (dateFieldFilter: ReportFilter) => {
        const periodValue = dateFieldFilter.rangeName;
        const updatedFilter = omit(dateFieldFilter, "value", "rangeName") as ReportFilter;
        updatedFilter.value = periodValue as ReportFilterValue;
        updatedFilter.operator = ReportFilterOperator.PERIOD;

        return { ...filter, [selectedDateField]: updatedFilter };
    };

    const { mutateAsync: saveReport, isLoading } = useMutation(async (report: Report) => {
        const dateFieldFilter = filter[selectedDateField];
        // This is required in case user selected a relative period like "last week", We don't want to save a date range with
        // absolute dates for this, instead we want to save it as a period which will show last week's data from the current date.
        const updatedFilters = dateFieldFilter?.rangeName ? getUpdatedFilterForPeriod(dateFieldFilter) : filter;

        const payload: Report = { ...report, filters: getQueryParams(updatedFilters), excludedColumns };

        if (report.id && modalState !== "openForSaveAs") {
            return api.put(`/sellers/${seller.id}/reports/${report.id}`, payload);
        }

        return api.post(`/sellers/${seller.id}/reports`, payload);
    });

    const handleSubmit = async (report: Report) => {
        const { data: savedReport } = await saveReport(report);
        invalidate(`reports`);
        setReport(savedReport as Report);
        setModalState("closed");
    };

    const handleSaveReportModalClose = () => {
        setModalState("closed");
    };

    const handleScheduleNewReport = () => setModalState("openForSaveSchedule");
    const isSchedulerConfigured = !!config.scheduler.url;

    return (
        <div className="flex w-full justify-end space-x-2">
            <PreviewModeGuard>
                {isSchedulerConfigured && (
                    <ScheduleReport
                        isOpen={modalState === "openForSaveSchedule"}
                        selectedDateField={selectedDateField}
                        report={report}
                        filter={filter}
                        onDateChange={onDateChange}
                        onScheduleNewReport={handleScheduleNewReport}
                    />
                )}
            </PreviewModeGuard>
            <SecondaryButton
                icon={<ExportIcon />}
                data-testid="reservations-report-export-button"
                onClick={() => setIsExportModalOpen(true)}
            >
                Export
            </SecondaryButton>
            <SubmitButton
                color={isWarning ? "warning" : "secondary"}
                variant={isWarning ? "standard" : "outline"}
                isLoading={isFetching}
                icon={<ArrowCcwIcon />}
                className="text-black"
                onClick={onRun}
            >
                Run Report
            </SubmitButton>

            <PreviewModeGuard>
                <PopoverList distance={7} trigger="click">
                    <SubmitButton isLoading={isLoading}>Save</SubmitButton>

                    <PopoverList.Item name="save" onClickItem={() => setModalState("openForSave")}>
                        Save
                    </PopoverList.Item>

                    {report.id ? (
                        <PopoverList.Item name="new" onClickItem={() => setModalState("openForSaveAs")}>
                            Save as New
                        </PopoverList.Item>
                    ) : null}
                </PopoverList>
            </PreviewModeGuard>

            <PreviewModeGuard>
                <SaveReportModal
                    isOpen={modalState !== "closed"}
                    report={report}
                    isLoading={isLoading}
                    isScheduledReport={modalState === "openForSaveSchedule"}
                    onSubmit={handleSubmit}
                    onClose={handleSaveReportModalClose}
                />
            </PreviewModeGuard>

            <CustomReportExportModal
                isOpen={isExportModalOpen}
                report={report}
                filter={filter}
                excludedColumns={excludedColumns}
                onClose={() => setIsExportModalOpen(false)}
            />
        </div>
    );
};

const getScheduleTitle = (cron: Cron, timezoneName: string) => {
    const interval = cron.interval.slice(1, -1);
    const intervalType = cron.interval.slice(-1);

    // dayjs parses the date but returns the same time, so need to convert the time to seller's timezone
    const startAt = utcToDate(cron.startAt, timezoneName);
    const diff = isBefore(startAt, now().startOf("day"))
        ? formatDate(startAt, DateFormat.DATE_TIME)
        : ` in ${startAt.fromNow(true)}`;

    return `Scheduled report: Next run ${diff}. Frequency: Every ${interval} ${
        Duration[intervalType as keyof typeof Duration]
    }s`;
};

interface ScheduleReportProps {
    report: Report;
    filter: ReportFilterState;
    selectedDateField: string;
    onDateChange: (value: JsDateRange) => void;
    isOpen: boolean;
    onScheduleNewReport: () => void;
}

const ScheduleReport = ({
    report,
    filter,
    isOpen,
    selectedDateField,
    onDateChange,
    onScheduleNewReport,
}: ScheduleReportProps) => {
    const user = useUser();
    const seller = useSeller();
    const invalidate = useInvalidate();
    const params = { seller: seller.id, user: user.id, remoteId: report.id };

    const { data: crons, isLoading: isFetchingCronJobs } = useScheduler<Cron>(["crons"], params, {
        enabled: !!report.id,
    });

    const cron = crons?.[0] ?? null;
    const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(isOpen);
    const [isCancelPromptOpen, setIsCancelPromptOpen] = useState(false);

    const { mutateAsync: deleteExport, isLoading: isDeleting } = useMutation(async (cron: Cron) => {
        return schedulerApi.delete(`/crons/${cron.id}`);
    });

    const handleScheduleReport = () => {
        if (report.id) {
            setIsScheduleModalOpen(true);
        } else {
            onScheduleNewReport();
        }
    };

    const handleScheduleDelete = async () => {
        const cronJob = Array.isArray(cron) && cron.length > 1 ? cron[0] : cron;
        await deleteExport(cronJob);
        setIsCancelPromptOpen(false);
        invalidate("crons");
    };

    if (isFetchingCronJobs) {
        return <Loader />;
    }

    return (
        <span>
            {isEmpty(cron) ? (
                <SecondaryButton icon={<ClockAltIcon />} onClick={handleScheduleReport}>
                    Schedule Now
                </SecondaryButton>
            ) : (
                <div className="inline-flex items-center justify-center rounded border border-gray-light px-2 font-semibold">
                    <div className="cursor-pointer" onClick={handleScheduleReport}>
                        {cron ? getScheduleTitle(cron, seller.timezoneName) : null}
                    </div>
                    <CloseIcon
                        className="ml-2 cursor-pointer"
                        color="red"
                        onClick={() => setIsCancelPromptOpen(true)}
                    />
                </div>
            )}
            <ScheduleReportModal
                isOpen={isScheduleModalOpen}
                selectedDateField={selectedDateField}
                report={report}
                filter={filter}
                cron={cron}
                onClose={() => setIsScheduleModalOpen(false)}
                onDateChange={onDateChange}
            />
            <PromptModal
                title="Cancel Scheduled Export"
                isLoading={isDeleting}
                isOpen={isCancelPromptOpen}
                onConfirm={handleScheduleDelete}
                onClose={() => setIsCancelPromptOpen(false)}
            >
                Are you sure you want to cancel this scheduled export?
            </PromptModal>
        </span>
    );
};
